import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { SITE_NAME, SITE_DESCRIPTION } from '@/mocks/site';

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const FORM_URL = 'https://readdy.ai/api/form/d9p1g12ftmtnfcfh0fsg';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;

    const honeypotField = form.querySelector('[name="website_alt"]') as HTMLInputElement;
    const honeypotValue = honeypotField?.value?.trim();
    if (honeypotValue) {
      setFormStatus('success');
      setContactForm({ name: '', email: '', message: '' });
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotField) formData.delete('website_alt');

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch { /* keep raw text */ }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setContactForm({ name: '', email: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const isSpam = typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('form data is spam'));
        setFormStatus('error');
        setFormError(isSpam ? '发送失败，请稍后重试' : (typeof serverMsg === 'string' ? serverMsg : '发送失败，请稍后重试'));
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${SITE_NAME}`,
    description: SITE_DESCRIPTION,
    url: 'https://hanhancomic.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: 'https://hanhancomic.com/',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50 pt-16">
        <Navbar />

        <main className="max-w-4xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Hero */}
          <section className="text-center py-10 md:py-14">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
              关于 {SITE_NAME}
            </h1>
            <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {SITE_NAME} 是专注于韩国漫画（韩漫）的中文在线阅读平台。我们致力于为广大漫画爱好者提供最全面、最高清、最流畅的下拉式韩漫阅读体验。
            </p>
          </section>

          {/* Brand Intro */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">品牌介绍</h2>
            <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                汗汗漫画成立于2020年，是国内最早一批专注于韩国漫画资源整合与分发的在线平台之一。经过多年的发展，我们已积累了超过20,000部优质韩漫资源，涵盖了恋爱甜宠、奇幻冒险、热血动作、悬疑推理、搞笑日常、后宫恋爱、剧情深度等众多类型。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                我们深知漫画爱好者的核心需求——画质清晰、加载流畅、更新及时。因此，汗汗漫画采用先进的下拉式阅读技术，让读者无需翻页即可畅快阅读，完美适配手机端和电脑端。
              </p>
              <p className="text-sm text-foreground-700 leading-relaxed">
                作为完全免费的漫画平台，我们坚持「让好漫画触手可及」的理念。无论是热门连载还是冷门佳作，你都能在汗汗漫画找到。我们的团队每天都会同步更新最新的漫画章节，确保读者第一时间看到心仪作品的最新内容。
              </p>
            </div>
          </section>

          {/* Features */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">我们的优势</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有漫画资源完全免费，无需付费即可畅享全部内容。我们承诺永远不向读者收费。' },
                { icon: 'ri-flashlight-line', title: '更新最快', desc: '与韩国同步更新，每日第一时间推送最新漫画章节。热门作品通常24小时内完成汉化。' },
                { icon: 'ri-smartphone-line', title: '下拉式阅读', desc: '采用先进的下拉式技术，适配手机和电脑端，无需翻页即可连续流畅阅读。' },
                { icon: 'ri-file-list-3-line', title: '海量资源', desc: '收录超过20,000部韩国漫画，覆盖所有主流类型。从热门神作到冷门精品一网打尽。' },
                { icon: 'ri-hd-line', title: '高清画质', desc: '全站漫画提供高清原画质，确保每一个细节都清晰可见，给你最佳的阅读体验。' },
                { icon: 'ri-user-heart-line', title: '读者至上', desc: '简洁无广告干扰的阅读界面，我们始终将读者的阅读体验放在第一位。' },
              ].map((feature) => (
                <div key={feature.title} className="bg-background-100 rounded-lg p-6 border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className={`${feature.icon} text-lg`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-14">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                { name: '小丸子', rating: 5, content: '用了汗汗漫画快两年了，更新速度真的很快，画质也清晰。最喜欢的是下拉式阅读，躺在床上看一天都不累！' },
                { name: '追更达人', rating: 5, content: '对比了好几个漫画平台，汗汗漫画的资源最全，很多冷门韩漫都能找到。而且完全免费，强烈推荐！' },
                { name: '漫画控', rating: 5, content: '从高中开始就在汗汗漫画上看韩漫，现在工作三年了还在用。界面简洁没有乱七八糟的广告，阅读体验特别好。' },
                { name: '深夜读者', rating: 4, content: '下拉式阅读真的香！晚上关灯看漫画体验超棒。希望继续增加更多优质韩漫资源，会一直支持！' },
              ].map((review) => (
                <div key={review.name} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                  <div className="flex items-center gap-2 mb-2">
                    {Array.from({ length: 5 }, (_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">&ldquo;{review.content}&rdquo;</p>
                  <p className="text-xs font-medium text-foreground-800">{review.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="mb-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { value: '2020年', label: '创立于' },
                { value: '20,000+', label: '漫画资源' },
                { value: '120万+', label: '活跃读者' },
                { value: '99.8%', label: '好评率' },
              ].map((s) => (
                <div key={s.label} className="text-center p-6 bg-background-100 rounded-lg border border-background-200/70">
                  <p className="text-2xl font-bold text-primary-600">{s.value}</p>
                  <p className="text-xs text-foreground-500 mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-14" id="contact">
            <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-6">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">邮箱地址</p>
                    <p className="text-sm text-foreground-600">contact@hanhancomic.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-customer-service-2-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">客服 QQ</p>
                    <p className="text-sm text-foreground-600">123456789</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">服务时间</p>
                    <p className="text-sm text-foreground-600">周一至周日 9:00 - 22:00</p>
                    <p className="text-xs text-foreground-500">节假日照常服务</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <form onSubmit={handleSubmit} className="bg-background-100 rounded-lg p-6 border border-background-200/70" data-readdy-form="">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入你的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1">
                        消息
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-all resize-none"
                        placeholder="请输入你的消息（500字以内）"
                      ></textarea>
                    </div>
                    {/* Honeypot */}
                    <div className="spam-protection-field">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-xs text-green-600 text-center mt-2">消息已发送成功，我们会尽快回复你！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-red-600 text-center mt-2">{formError || '发送失败，请稍后重试'}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}